import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  ROLE_GROUP,
  type CoachingDashboardFiltersResponse,
  type CoachSummary,
  type FilterRole,
  type RoleGroup,
} from '@backrs_ui/grow-ui/src/redux/coachingDashboard';
import { personName } from '@backrs_ui/grow-ui/src/utils/personName';

const { columns } = appStrings.modules.coachingDashboard;

/** A tab's identity doubles as the role group sent to the server. */
export const ROSTER_TAB = ROLE_GROUP;
export type RosterTab = RoleGroup;

export const ROSTER_TABS: readonly RosterTab[] = [ROSTER_TAB.STUDENTS, ROSTER_TAB.COACHES];

export const parseRosterTab = (value: string | null): RosterTab =>
  value === ROSTER_TAB.COACHES ? ROSTER_TAB.COACHES : ROSTER_TAB.STUDENTS;

/** The roles a tab's group selects on, or all of them where the response
 * carries no groups. */
export const rolesForTab = (
  tab: RosterTab,
  filterOptions: CoachingDashboardFiltersResponse | undefined,
): FilterRole[] => {
  const roles = filterOptions?.roles ?? [];
  const groupRoleIds = filterOptions?.role_group_ids?.[tab];

  return groupRoleIds ? roles.filter((role) => groupRoleIds.includes(role.id)) : roles;
};

/** Column labels per tab, in render order. */
export const ROSTER_COLUMNS = {
  [ROSTER_TAB.STUDENTS]: {
    name: columns.name,
    coach: columns.coach,
    program: columns.program,
    grade: columns.grade,
    school: columns.school,
    institution: columns.institution,
    assessments: columns.assessments,
    lastLogin: columns.lastLogin,
  },
  [ROSTER_TAB.COACHES]: {
    name: columns.name,
    institution: columns.institution,
    studentCount: columns.studentCount,
  },
} as const;

export const coachTooltipLabel = (coach: CoachSummary, backrsLabel: string): string => {
  const name = personName(coach);

  return coach.is_backrs_coach ? `${name} · ${backrsLabel}` : name;
};

/** Backrs coaches first, everyone else in their existing order, so a Backrs
 * coach is never the one hidden behind an overflow count. */
export const orderCoaches = (coaches: readonly CoachSummary[]): CoachSummary[] => [
  ...coaches.filter((coach) => coach.is_backrs_coach),
  ...coaches.filter((coach) => !coach.is_backrs_coach),
];
